import { useEffect, useRef } from 'react';
import { authenticateByToken } from './allauth';
import { captureEvent } from '@/posthog-analytics/posthog';
import { getSafeErrorForAnalytics } from '@/common/utils';

/**
 * Google One Tap / FedCM automatic sign-in for ATS users (REC-1543).
 *
 * This is an *additive* layer on top of the existing "Sign in with Google"
 * button. For a returning recruiter / HM who is already logged into Google in a
 * supporting browser (Chrome desktop), Google Identity Services shows a brief,
 * browser-controlled prompt and issues an ID token automatically — we exchange
 * it for a session with no button click.
 *
 * ## Why a same-site credential-broker iframe
 *
 * Google checks the *page origin* against the OAuth client's Authorized
 * JavaScript origins, and per-tenant subdomains (`<tenant>.<base>`) are NOT
 * registered (wildcards are forbidden, so per-tenant registration doesn't
 * scale). Only the canonical auth origin — `auth.<base>` (e.g.
 * `auth.localr41.com`, the same host the existing Google button OAuth uses) —
 * is registered. FedCM supports One Tap in a cross-origin iframe *only when
 * same-site*; `<tenant>.<base>` and `auth.<base>` share the registrable domain
 * `<base>`, so we run GSI inside an iframe served from `auth.<base>` and surface
 * the prompt over the tenant page.
 *
 * The iframe is a thin broker: it runs GSI and posts the raw ID token back to
 * this parent. We then exchange it via `authenticateByToken` against *our own*
 * tenant host, so the server-issued nonce (bound to this page's session) and the
 * tenant-membership gate stay exactly as built — the backend is unchanged.
 *
 * It deliberately no-ops everywhere it cannot help:
 *  - when a live session already exists (caller passes enabled=false),
 *  - when the server returns no client id (auto sign-in unavailable),
 *  - on Safari / Firefox (GSI/FedCM itself declines to auto-select there).
 *
 * The "Sign in with Google" button and email/password flows remain the floor
 * and keep working everywhere.
 */

const CONFIG_URL = '/api/public/auth/google-one-tap-config';
const FRAME_PATH = '/auth/one-tap-frame';

// Messages exchanged with the broker iframe.
const MSG_CREDENTIAL = 'r41-one-tap-credential';
const MSG_READY = 'r41-one-tap-ready';
const MSG_ERROR = 'r41-one-tap-error';
const MSG_DISABLED = 'r41-one-tap-disabled';

// Cap how long logout waits for the opt-out iframe before navigating anyway.
const DISABLE_TIMEOUT_MS = 1500;

interface OneTapConfig {
  google_client_id: string;
  nonce: string;
}

interface CredentialMessage {
  type: typeof MSG_CREDENTIAL;
  credential: string;
  select_by?: string | null;
}

async function fetchOneTapConfig(): Promise<OneTapConfig | null> {
  try {
    const resp = await fetch(CONFIG_URL, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as OneTapConfig;
    if (!data?.google_client_id) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Compute the canonical auth origin (`https://auth.<base>`) from the current
 * host by swapping the tenant label for `auth` and keeping the base domain.
 * Env-agnostic: works for localr41.com / devr41.com / qar41.com / recruit41.com.
 *
 *   demo.localr41.com    -> https://auth.localr41.com
 *   acme.recruit41.com   -> https://auth.recruit41.com
 *   localr41.com         -> https://auth.localr41.com (apex, no tenant label)
 *
 * Returns null if the host has no resolvable base (e.g. bare `localhost`), in
 * which case One Tap simply no-ops and the button fallback stands.
 */
export function computeAuthOrigin(host: string): string | null {
  const labels = host.split('.');
  // Need at least a registrable domain (e.g. `example.com`).
  if (labels.length < 2) return null;
  // Base domain = last two labels (eTLD+1 for our .com domains).
  const base = labels.slice(-2).join('.');
  if (labels[0] === 'auth') {
    // Already on the auth origin (shouldn't happen for the parent, but safe).
    return `https://${host}`;
  }
  return `https://auth.${base}`;
}

/**
 * Call on logout (and only on logout) to stop One Tap from immediately
 * re-signing the user back in (EC-1: the logout loop).
 *
 * GSI lives inside the broker iframe on the `auth.<base>` origin, so
 * `disableAutoSelect()` (which sets the `g_state` opt-out cookie on the *caller*
 * origin) must be invoked there, not on this tenant page. We briefly mount the
 * broker iframe in disable mode; it calls `disableAutoSelect()` then posts back.
 *
 * Returns a promise that resolves once the iframe confirms the opt-out (or after
 * a short timeout) — callers MUST await it before navigating away, otherwise the
 * page often unloads before GSI finishes and `g_state` is never set, leaving the
 * logout loop unfixed. Always resolves (never rejects); logout must not block on
 * a slow/absent GSI.
 */
export function disableGoogleAutoSelect(): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    let frame: HTMLIFrameElement | null = null;
    let handler: ((e: MessageEvent) => void) | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (handler) window.removeEventListener('message', handler);
      if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
      resolve();
    };

    try {
      const authOrigin = computeAuthOrigin(window.location.hostname);
      if (!authOrigin) {
        resolve();
        return;
      }
      handler = (event: MessageEvent) => {
        if (event.origin !== authOrigin) return;
        const data = event.data as { type?: string } | null;
        if (data?.type === MSG_DISABLED) finish();
      };
      window.addEventListener('message', handler);

      const params = new URLSearchParams({
        mode: 'disable',
        parentOrigin: window.location.origin,
      });
      frame = document.createElement('iframe');
      frame.src = `${authOrigin}${FRAME_PATH}?${params.toString()}`;
      frame.setAttribute('allow', 'identity-credentials-get');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.display = 'none';
      document.body.appendChild(frame);
      // Don't block logout indefinitely if GSI is slow or unavailable.
      window.setTimeout(finish, DISABLE_TIMEOUT_MS);
    } catch {
      finish(); // Best-effort — never block logout on this.
    }
  });
}

interface UseGoogleOneTapOptions {
  /** Only attempt when true (e.g. ATS dashboard login + unauthenticated). */
  enabled: boolean;
  /** Called after a successful auto sign-in to re-sync auth state. */
  onSuccess?: () => void;
}

export function useGoogleOneTap({
  enabled,
  onSuccess,
}: UseGoogleOneTapOptions): void {
  // Hold the latest onSuccess in a ref so it is NOT an effect dependency.
  // `refreshAuth` (the usual onSuccess) is not memoised and changes identity on
  // every AuthProvider re-render; keeping it out of the deps lets the effect run
  // once per real mount. Depending on it forced a survives-cleanup `attempted`
  // guard which broke under React StrictMode's mount→cleanup→remount in dev
  // (the iframe was torn down and never recreated → no prompt locally).
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let iframe: HTMLIFrameElement | null = null;
    let messageHandler: ((e: MessageEvent) => void) | null = null;
    let exchanged = false;

    const cleanup = () => {
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
        messageHandler = null;
      }
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      iframe = null;
    };

    (async () => {
      const config = await fetchOneTapConfig();
      if (cancelled || !config) return;

      const authOrigin = computeAuthOrigin(window.location.hostname);
      if (!authOrigin) return;

      const parentOrigin = window.location.origin;

      const exchangeCredential = async (
        credential: string,
        selectBy?: string | null,
      ) => {
        if (exchanged) return;
        exchanged = true;
        captureEvent('ats_auto_signin_attempted', {
          select_by: selectBy ?? undefined,
        });
        try {
          // Exchange the ID token for a session via the existing allauth headless
          // Google token endpoint — on THIS tenant host. The server verifies
          // aud/iss/exp + signature (Google lib), the single-use nonce (bound to
          // this page's session), and enforces the tenant-membership gate before
          // minting a session. We never log the id_token.
          const result = await authenticateByToken('google', {
            id_token: credential,
            client_id: config.google_client_id,
          });
          if (result?.meta?.is_authenticated || result?.status === 200) {
            captureEvent('ats_auto_signin_succeeded', {});
            cleanup();
            if (onSuccessRef.current) {
              onSuccessRef.current();
            } else {
              window.location.reload();
            }
          } else {
            // 401/403 → no access / nonce invalid. Stay on the button fallback.
            captureEvent('ats_auto_signin_failed', {
              stage: 'token_exchange',
              status: result?.status,
            });
            cleanup();
          }
        } catch (err) {
          captureEvent('ats_auto_signin_failed', {
            stage: 'token_exchange',
            error: getSafeErrorForAnalytics(err),
          });
          cleanup();
        }
      };

      messageHandler = (event: MessageEvent) => {
        // Trust a credential ONLY when it comes from the exact registered auth
        // origin we embedded — never from any other frame.
        if (event.origin !== authOrigin) return;
        const data = event.data as Partial<CredentialMessage> & {
          type?: string;
        };
        if (!data || typeof data.type !== 'string') return;

        if (data.type === MSG_CREDENTIAL) {
          if (typeof data.credential !== 'string' || !data.credential) return;
          void exchangeCredential(data.credential, data.select_by ?? null);
        } else if (data.type === MSG_ERROR) {
          captureEvent('ats_auto_signin_failed', { stage: 'iframe_gsi' });
          cleanup();
        } else if (data.type === MSG_READY) {
          // Broker is live; nothing to do — GSI owns the prompt UI from here.
        }
      };
      window.addEventListener('message', messageHandler);

      if (cancelled) {
        cleanup();
        return;
      }

      // Build the broker iframe URL. The iframe validates parentOrigin server-
      // and client-side before posting any credential back.
      const params = new URLSearchParams({
        client_id: config.google_client_id,
        nonce: config.nonce,
        parentOrigin,
      });
      const frame = document.createElement('iframe');
      frame.src = `${authOrigin}${FRAME_PATH}?${params.toString()}`;
      // Required so FedCM / One Tap may run inside this cross-origin (same-site)
      // iframe.
      frame.setAttribute('allow', 'identity-credentials-get');
      frame.title = 'Google sign-in';
      // The One Tap prompt is rendered by the browser as top-level chrome, not
      // inside the frame box, so the frame itself can stay invisible.
      frame.style.position = 'fixed';
      frame.style.top = '0';
      frame.style.right = '0';
      frame.style.width = '1px';
      frame.style.height = '1px';
      frame.style.border = '0';
      frame.style.opacity = '0';
      frame.style.pointerEvents = 'none';
      frame.setAttribute('aria-hidden', 'true');
      iframe = frame;
      document.body.appendChild(frame);
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [enabled]);
}
